/**
 * Replica-lag correction for CRM deal moves.
 * Supabase read replica can return stale stage_id for 7–15+ seconds after a write.
 * Used by: board query (patchBoardDeals), per-stage API response (applyPatchToStageDeals).
 * TTL aligned with CRM.tsx MOVE_GUARD_MS – sync effect skips overwrite during this window.
 */
const MOVE_TTL_MS = 30_000;

interface MoveInfo {
  dealId: string;
  stageId: string;
  pipelineId?: string;
  deal: any;
}

const state: {
  dealId: string | null;
  stageId: string | null;
  pipelineId: string | null;
  deal: any;
  timestamp: number;
} = {
  dealId: null,
  stageId: null,
  pipelineId: null,
  deal: null,
  timestamp: 0,
};

const STORAGE_KEY = "crmLastMovedDeal";

/** Call after a successful deal move (CRM or Deal page). */
export function recordMove(dealId: string, stageId: string, pipelineId?: string, deal?: any): void {
  if (typeof window !== "undefined" && (window.location.search.includes("crm_debug=1") || localStorage.getItem("crm_debug") === "1")) {
    console.log("[CRM-DEBUG] 3.recordMove", dealId, { stageId, pipelineId });
  }
  state.dealId = dealId;
  state.stageId = stageId;
  state.pipelineId = pipelineId ?? null;
  state.deal = deal ?? null;
  state.timestamp = Date.now();
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ id: dealId, stageId, pipelineId, t: state.timestamp }));
    setTimeout(() => sessionStorage.removeItem(STORAGE_KEY), MOVE_TTL_MS);
  } catch {
    /* ignore */
  }
}

/** Get last move info if still valid (within 60s). Used for replica-lag patch. */
export function getLastMoveInfo(allDealsFromBoard?: any[]): MoveInfo | null {
  const now = Date.now();
  if (state.dealId && state.stageId && now - state.timestamp < MOVE_TTL_MS) {
    const deal = state.deal ?? (allDealsFromBoard?.find((d: any) => d.id === state.dealId));
    if (deal) {
      return {
        dealId: state.dealId,
        stageId: state.stageId,
        pipelineId: state.pipelineId ?? undefined,
        deal,
      };
    }
  }
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.id || !parsed?.stageId || now - (parsed.t ?? 0) > MOVE_TTL_MS) return null;
    const deal =
      allDealsFromBoard?.find((d: any) => d.id === parsed.id) ??
      (state.dealId === parsed.id ? state.deal : null);
    if (!deal) return null;
    return {
      dealId: parsed.id,
      stageId: parsed.stageId,
      pipelineId: parsed.pipelineId,
      deal,
    };
  } catch {
    return null;
  }
}

/**
 * Apply replica-lag patch to a stage's deal list.
 * - If this is the expected stage and the moved deal is missing → add it (unless appendOnly).
 * - If this is NOT the expected stage and the moved deal is present → remove it.
 * @param appendOnly When true (e.g. load more), only filter out the moved deal from wrong stage; never add.
 */
export function applyPatchToStageDeals(
  deals: any[],
  stageId: string,
  move: MoveInfo | null,
  options?: { appendOnly?: boolean }
): any[] {
  if (!move) return deals;
  const { dealId, stageId: expectedStageId, deal } = move;
  if (typeof window !== "undefined" && (window.location.search.includes("crm_debug=1") || localStorage.getItem("crm_debug") === "1") && sessionStorage.getItem("crm_debug_track") === dealId) {
    const inDeals = deals.some((d: any) => d.id === dealId);
    console.log("[CRM-DEBUG] applyPatchToStageDeals", dealId, { stageId, expectedStageId, inDeals, appendOnly: options?.appendOnly });
  }
  if (stageId === expectedStageId) {
    if (options?.appendOnly) return deals;
    if (deals.some((d: any) => d.id === dealId)) return deals;
    return [{ ...deal, stageId, stage_id: stageId }, ...deals];
  }
  return deals.filter((d: any) => d.id !== dealId);
}

/** Patch board deals array: correct stage_id for the moved deal if API returned stale value. */
export function patchBoardDeals(deals: any[]): any[] {
  const info = getLastMoveInfo(deals);
  if (!info || !deals?.length) return deals;
  const idx = deals.findIndex((d: any) => d.id === info.dealId);
  if (idx < 0) return deals;
  if (typeof window !== "undefined" && (window.location.search.includes("crm_debug=1") || localStorage.getItem("crm_debug") === "1")) {
    const d = deals[idx] as any;
    const before = d.stage_id ?? d.stageId;
    console.log("[CRM-DEBUG] patchBoardDeals", info.dealId, { before, after: info.stageId, wasStale: before !== info.stageId });
  }
  const d = deals[idx] as any;
  const currentStage = d.stageId ?? d.stage_id;
  const currentPipeline = d.pipelineId ?? d.pipeline_id;
  const patch: Record<string, string> = {};
  if (currentStage !== info.stageId) {
    patch.stageId = info.stageId;
    patch.stage_id = info.stageId;
  }
  if (info.pipelineId && currentPipeline !== info.pipelineId) {
    patch.pipelineId = info.pipelineId;
    patch.pipeline_id = info.pipelineId;
  }
  if (Object.keys(patch).length === 0) return deals;
  const next = [...deals];
  next[idx] = { ...d, ...patch };
  return next;
}

/** Headers for API when we have a recent move (server-side correction). */
export function getBoardDebugHeaders(): Record<string, string> {
  const now = Date.now();
  if (state.dealId && state.stageId && now - state.timestamp < MOVE_TTL_MS) {
    const h: Record<string, string> = {
      "X-Debug-Last-Moved-Deal": state.dealId,
      "X-Debug-Expected-Stage": state.stageId,
    };
    if (state.pipelineId) h["X-Debug-Expected-Pipeline"] = state.pipelineId;
    return h;
  }
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed?.id || !parsed?.stageId || now - (parsed.t ?? 0) > MOVE_TTL_MS) return {};
    const h: Record<string, string> = {
      "X-Debug-Last-Moved-Deal": parsed.id,
      "X-Debug-Expected-Stage": parsed.stageId,
    };
    if (parsed.pipelineId) h["X-Debug-Expected-Pipeline"] = parsed.pipelineId;
    return h;
  } catch {
    return {};
  }
}
